import { useCallback, useEffect, useRef, useState } from 'react';

const EDGE_GAP = 8;

const clampPosition = (position, size) => ({
  left: Math.min(Math.max(EDGE_GAP, position.left), Math.max(EDGE_GAP, window.innerWidth - size - EDGE_GAP)),
  top: Math.min(Math.max(EDGE_GAP, position.top), Math.max(EDGE_GAP, window.innerHeight - size - EDGE_GAP))
});

const getInitialPosition = (storageKey, size, side, bottomOffset) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (Number.isFinite(saved?.left) && Number.isFinite(saved?.top)) return clampPosition(saved, size);
  } catch (error) {}

  return clampPosition({
    left: side === 'left' ? 24 : window.innerWidth - size - 24,
    top: window.innerHeight - size - bottomOffset
  }, size);
};

const useDraggablePosition = ({ storageKey, size, side = 'right', bottomOffset = 24 }) => {
  const [position, setPosition] = useState(() => getInitialPosition(storageKey, size, side, bottomOffset));
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    const handleResize = () => setPosition((current) => clampPosition(current, size));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [size]);

  const onPointerDown = useCallback((event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      left: position.left,
      top: position.top,
      moved: false
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [position.left, position.top]);

  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(deltaX, deltaY) < 5) return;
    drag.moved = true;
    setIsDragging(true);
    setPosition(clampPosition({ left: drag.left + deltaX, top: drag.top + deltaY }, size));
    event.preventDefault();
  }, [size]);

  const onPointerUp = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.moved) {
      const finalPosition = clampPosition({
        left: drag.left + event.clientX - drag.startX,
        top: drag.top + event.clientY - drag.startY
      }, size);
      setPosition(finalPosition);
      try { localStorage.setItem(storageKey, JSON.stringify(finalPosition)); } catch (error) {}
      suppressClickRef.current = true;
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
    }
    dragRef.current = null;
    setIsDragging(false);
  }, [size, storageKey]);

  const consumeDraggedClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  return {
    position,
    isDragging,
    consumeDraggedClick,
    pointerHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp
    }
  };
};

export default useDraggablePosition;